import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const activityTimer = useRef(null);

  const clearSession = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('lastActivity');
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const resetActivityTimer = useCallback(() => {
    if (!token) return;
    localStorage.setItem('lastActivity', Date.now().toString());
    if (activityTimer.current) clearTimeout(activityTimer.current);
    activityTimer.current = setTimeout(() => {
      clearSession();
      window.location.href = '/login';
    }, SESSION_TIMEOUT_MS);
  }, [token, clearSession]);

  // Check session validity on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const lastActivity = localStorage.getItem('lastActivity');

    if (storedToken && storedUser) {
      // Check if session has expired based on last activity
      if (lastActivity && (Date.now() - parseInt(lastActivity)) > SESSION_TIMEOUT_MS) {
        clearSession();
      } else {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      }
    }
    setLoading(false);
  }, [clearSession]);

  // Track user activity for session timeout
  useEffect(() => {
    if (!token) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    const handleActivity = () => resetActivityTimer();

    events.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));
    resetActivityTimer();

    return () => {
      events.forEach(e => window.removeEventListener(e, handleActivity));
      if (activityTimer.current) clearTimeout(activityTimer.current);
    };
  }, [token, resetActivityTimer]);

  // Global axios interceptor for 401 responses (skip login endpoint)
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        const requestUrl = error.config?.url || '';
        const isLoginRequest = requestUrl.includes('/api/auth/login');
        if (error.response?.status === 401 && !isLoginRequest) {
          clearSession();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [clearSession]);

  const login = async (email, password) => {
    const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
      email,
      password
    });

    const { user: userData, token: authToken } = response.data;
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', authToken);
    localStorage.setItem('lastActivity', Date.now().toString());
    axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
    return userData;
  };

  const logout = () => {
    clearSession();
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
